import { BudgetPhases } from 'business/constants';
import { useState, useEffect, useContext } from 'react';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { useExportInscriptionsExcelForReimport } from 'routes/BudgetModule/helpers/export';
import { useGetBudgetProposalsInscriptions } from 'routes/BudgetModule/helpers/hooks/useGetInscriptions';
import { BudgetProposalWithLabel } from '../GlobalExport';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

type Props = {
    selectedBudgetProposals: BudgetProposalWithLabel[];
    allBudgetProposals: BudgetProposalWithLabel[];
    selectedPhase: BudgetPhases | null;
};

/**
 * Hook to manage fetch and export of inscriptions from multiple budget proposals
 */
export const useGlobalExportHook = ({
    selectedBudgetProposals,
    allBudgetProposals,
    selectedPhase,
}: Props) => {
    const [isExporting, setIsExporting] = useState(false);
    const statusesSettings = useContext(StatusSettingsContext);
    const firstItem =
        selectedBudgetProposals.length > 0
            ? selectedBudgetProposals[0]
            : allBudgetProposals[0];
    // arbitratry but satisfies product requirements
    const firstBy = firstItem.by;
    const firstExercice = firstItem.exercice;

    const budgetProposalsIds = selectedBudgetProposals.map((bp) => bp.id);
    const selectedPhaseOrPlaceholder =
        selectedPhase !== null
            ? selectedPhase
            : BudgetPhases.PROPOSITION_SERVICES;

    const {
        data: inscriptionsData = { data: [], columns: [] },
        isLoading: areInscriptionsLoading,
    } = useGetBudgetProposalsInscriptions({
        budgetBy: firstBy,
        budgetProposalIds: budgetProposalsIds,
        phase: selectedPhaseOrPlaceholder,
        enabled: isExporting,
    });

    const secondaryBudgetAxisSourceTable =
        useSelectSecondaryBudgetAxisSourceTable();

    const exportInscriptionsExcelForReimport =
        useExportInscriptionsExcelForReimport({
            inscriptionsData,
            phase: selectedPhaseOrPlaceholder,
            statusesSettings,
            secondaryBudgetAxisSourceTable,
            exercice: firstExercice,
        });

    const shouldExportStart =
        isExporting && inscriptionsData && !areInscriptionsLoading;

    useEffect(() => {
        if (shouldExportStart) {
            setIsExporting(false);
            exportInscriptionsExcelForReimport();
        }
    }, [shouldExportStart, exportInscriptionsExcelForReimport]);

    return {
        isExporting,
        setIsExporting,
    };
};
